import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Container,
  Stack,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Badge,
  ListItemIcon
} from '@mui/material';
import {
  Security,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Shield,
  ExpandMore,
  Download,
  Assessment,
  AutoAwesome,
  CheckBoxOutlineBlank,
  History,
  Delete as DeleteIcon,
  Refresh,
  Info,
  Code,
  Build,
  AccountBalance,
  LocalHospital,
  Business,
  Gavel,
  ArrowBack,
  CloudDownload,
  AttachFile,
  CloudUpload,
  InsertDriveFile,
  Storage,
  UploadFile,
  Add,
  Edit as EditIcon,
  DeleteOutline
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

// Resolve icon string → MUI component
const _RB_ICON_MAP = { Security, Shield, AccountBalance, LocalHospital, Gavel, Assessment, Business };
const RBIcon = ({ name, ...props }) => {
  const Icon = _RB_ICON_MAP[name] || Security;
  return <Icon {...props} />;
};

// UI metadata for framework icons and colors (keyed by framework_id)
const FRAMEWORK_UI_META = {
  'dpdp-2023':    { icon: 'Shield',          color: '#FF9933', industry: 'India-Specific' },
  'pci-dss':      { icon: 'AccountBalance',  color: '#d32f2f', industry: 'Financial' },
  'hipaa':        { icon: 'LocalHospital',   color: '#388e3c', industry: 'Healthcare' },
  'gdpr':         { icon: 'Shield',          color: '#7b1fa2', industry: 'Privacy' },
  'soc2':         { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'iso27001':     { icon: 'Gavel',           color: '#1976d2', industry: 'Technology' },
  'nist-csf':     { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'fedramp':      { icon: 'Shield',          color: '#1976d2', industry: 'Technology' },
  'cis-controls': { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'soc2-type1':   { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'soc2-type2':   { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'iso27701':     { icon: 'Shield',          color: '#7b1fa2', industry: 'Privacy' },
  'cert-in':      { icon: 'Shield',          color: '#FF9933', industry: 'India-Specific' },
  'rbi-framework':{ icon: 'AccountBalance',  color: '#FF9933', industry: 'India-Specific' },
  'sox':          { icon: 'AccountBalance',  color: '#d32f2f', industry: 'Financial' },
  'iso27017':     { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'iso27018':     { icon: 'Shield',          color: '#7b1fa2', industry: 'Privacy' },
  'csa-star':     { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'owasp-top10':  { icon: 'Security',        color: '#1976d2', industry: 'Technology' },
  'iso22301':     { icon: 'Gavel',           color: '#e65100', industry: 'Business Continuity' },
};
const DEFAULT_META = { icon: 'Security', color: '#1976d2', industry: 'Technology' };

function RegulatoryBot() {
  const { showNotification } = useNotifications();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [checklist, setChecklist] = useState([]);
  const [checklistPage, setChecklistPage] = useState(0);
  const CHECKS_PER_PAGE = 25;
  const [generatingEvidence, setGeneratingEvidence] = useState(false);
  const [error, setError] = useState(null);

  const [evidenceHistory, setEvidenceHistory] = useState([]);
  const [frameworkHistory, setFrameworkHistory] = useState([]); // Framework-specific history
  const [showHistory, setShowHistory] = useState(false);
  const [showFrameworkHistory, setShowFrameworkHistory] = useState(false);
  const [expandedFramework, setExpandedFramework] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('All');
  const [remediationDialog, setRemediationDialog] = useState({ open: false, check: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, status: null, title: null });
  const [downloadingChecks, setDownloadingChecks] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseLoading, setLicenseLoading] = useState(true);
  const [syncStatus, setSyncStatus] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const [complianceFrameworks, setComplianceFrameworks] = useState([]);
  const [frameworksLoading, setFrameworksLoading] = useState(true);

  // Evidence document state
  const [evidenceDocs, setEvidenceDocs] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null); // checkId currently uploading
  const [s3BrowseDialog, setS3BrowseDialog] = useState({ open: false, checkId: null, files: [], loading: false, error: null });
  const fileInputRef = React.useRef(null);
  const [pendingUploadCheckId, setPendingUploadCheckId] = useState(null);
  const [evidenceBucket, setEvidenceBucket] = useState(null); // null = not loaded, '' = not configured, 'name' = configured
  const [evidenceBucketInput, setEvidenceBucketInput] = useState('');
  const [evidenceBucketLoading, setEvidenceBucketLoading] = useState(false);
  const [evidenceBucketSaving, setEvidenceBucketSaving] = useState(false);

  // Admin checklist upload state — supports multiple files
  const [uploadChecklistDialog, setUploadChecklistDialog] = useState(false);
  const [uploadChecklistFiles, setUploadChecklistFiles] = useState([]); // array of File objects
  const [uploadChecklistLoading, setUploadChecklistLoading] = useState(false);
  const [uploadChecklistResult, setUploadChecklistResult] = useState(null);
  const [uploadElapsedSecs, setUploadElapsedSecs] = useState(0);
  const checklistFileInputRef = React.useRef(null);

  // Admin manual check add/edit state
  const [checkDialog, setCheckDialog] = useState({ open: false, mode: 'add', check: null });
  const [checkForm, setCheckForm] = useState({ title: '', description: '', severity: 'MEDIUM', category: '', aws_service: '', requirement_section: '', official_reference: '' });
  const [checkSaving, setCheckSaving] = useState(false);

  // Loaded upload info for current framework (admin only — array of uploads)
  const [checklistUploads, setChecklistUploads] = useState([]);

  // Admin framework assignment state
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignSelectedUser, setAssignSelectedUser] = useState('');
  const [assignUserFrameworks, setAssignUserFrameworks] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);

  // Admin framework add/delete state
  const [addFrameworkDialog, setAddFrameworkDialog] = useState(false);
  const [addFrameworkForm, setAddFrameworkForm] = useState({ framework_id: '', name: '', description: '', official_source_url: '' });
  const [addFrameworkSaving, setAddFrameworkSaving] = useState(false);
  const [deleteFrameworkDialog, setDeleteFrameworkDialog] = useState({ open: false, framework: null });
  const [deleteFrameworkSaving, setDeleteFrameworkSaving] = useState(false);

  const formatRelativeTime = (isoString) => {
    const diff = Date.now() - new Date(isoString).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 30) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(isoString).toLocaleDateString();
  };

  // Industry categories for filtering (matching Compliance module)
  const INDUSTRY_CATEGORIES = [
    { id: 'All', name: 'All Frameworks', icon: <Security /> },
    { id: 'India-Specific', name: 'India-Specific', icon: <Shield />, color: '#FF9933' },
    { id: 'Financial', name: 'Financial & Audit', icon: <AccountBalance />, color: '#d32f2f' },
    { id: 'Healthcare', name: 'Healthcare', icon: <LocalHospital />, color: '#388e3c' },
    { id: 'Privacy', name: 'Privacy & Data Protection', icon: <Shield />, color: '#7b1fa2' },
    { id: 'Business Continuity', name: 'Business Continuity', icon: <Security />, color: '#e65100' },
    { id: 'Government', name: 'Government', icon: <Shield />, color: '#1565c0' },
    { id: 'Technology', name: 'Technology', icon: <Security />, color: '#1976d2' }
  ];

  // Build dynamic categories from framework data — add any new categories from the DB
  const dynamicCategories = React.useMemo(() => {
    const knownIds = new Set(INDUSTRY_CATEGORIES.map(c => c.id));
    const extra = [];
    for (const fw of complianceFrameworks) {
      if (fw.category && !knownIds.has(fw.category)) {
        knownIds.add(fw.category);
        extra.push({ id: fw.category, name: fw.category, icon: <Security />, color: fw.color || '#1976d2' });
      }
    }
    return [...INDUSTRY_CATEGORIES, ...extra];
  }, [complianceFrameworks]);

  // Map frameworks to industries — use DB category, fallback to FRAMEWORK_UI_META
  const getFrameworkIndustry = (frameworkId) => {
    const fw = complianceFrameworks.find(f => f.id === frameworkId);
    if (fw?.category) return fw.category;
    const meta = FRAMEWORK_UI_META[frameworkId];
    return meta ? meta.industry : 'Technology';
  };

  // Filter frameworks by industry
  const filteredFrameworks = selectedIndustry === 'All' 
    ? complianceFrameworks 
    : complianceFrameworks.filter(f => getFrameworkIndustry(f.id) === selectedIndustry);



  // Helper function to format evidence as a clean summary
  const formatEvidenceSummary = (item) => {
    if (!item.evidence) return '';
    
    // Extract the first sentence or up to 150 characters as summary
    const evidence = item.evidence;
    const firstSentence = evidence.split(/[.!?]/)[0];
    
    if (firstSentence.length > 150) {
      return firstSentence.substring(0, 150) + '...';
    }
    
    return firstSentence + (evidence.includes('.') ? '.' : '');
  };

  // Helper function to extract resource counts from evidence
  const getResourceCounts = (item) => {
    const counts = { passed: 0, failed: 0, total: 0 };
    
    if (!item.resourcesAssessed && !item.evidence) return counts;
    
    // Try to extract from resourcesAssessed field
    const resourcesText = item.resourcesAssessed || '';
    const totalMatch = resourcesText.match(/(\d+)\s+resources?/i);
    if (totalMatch) {
      counts.total = parseInt(totalMatch[1]);
    }
    
    // Calculate from affectedResources
    const affected = item.affectedResources || '';
    if (affected.toLowerCase().includes('none') || affected.toLowerCase().includes('all resources are compliant')) {
      counts.passed = counts.total;
      counts.failed = 0;
    } else if (affected.toLowerCase().includes('all') && affected.toLowerCase().includes('non-compliant')) {
      counts.failed = counts.total;
      counts.passed = 0;
    } else {
      // Extract number from "X resources: ..."
      const failedMatch = affected.match(/(\d+)\s+resources?:/i);
      if (failedMatch) {
        counts.failed = parseInt(failedMatch[1]);
        counts.passed = counts.total - counts.failed;
      }
    }
    
    return counts;
  };

  // Helper function to format affected resources as a numbered list
  const formatAffectedResources = (affectedResources) => {
    if (!affectedResources || affectedResources === 'Pending analysis...') {
      return (
        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
          Pending analysis...
        </Typography>
      );
    }
    
    // If no affected resources (compliant)
    if (affectedResources.toLowerCase().includes('none') || 
        affectedResources.toLowerCase().includes('all resources are compliant')) {
      return (
        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'success.main', fontWeight: 500 }}>
          ✓ All resources are compliant
        </Typography>
      );
    }
    
    // If no resources to assess
    if (affectedResources.toLowerCase().includes('no resources found')) {
      return (
        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          No resources found to assess
        </Typography>
      );
    }
    
    // Extract resource list from format "X resources: res1, res2, res3"
    const match = affectedResources.match(/:\s*(.+?)(?:\s*\(and \d+ more\))?$/);
    if (match) {
      const resourceList = match[1].split(',').map(r => r.trim()).filter(r => r);
      
      return (
        <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: '0.75rem' }}>
          {resourceList.map((resource, idx) => (
            <li key={idx} style={{ marginBottom: '2px' }}>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary' }}>
                {resource}
              </Typography>
            </li>
          ))}
          {affectedResources.includes('and') && affectedResources.match(/\(and (\d+) more\)/)?.[1] && (
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5, display: 'block' }}>
              ...and {affectedResources.match(/\(and (\d+) more\)/)[1]} more
            </Typography>
          )}
        </Box>
      );
    }
    
    // Fallback: display as-is
    return (
      <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.primary', wordBreak: 'break-word' }}>
        {affectedResources}
      </Typography>
    );
  };

  useEffect(() => {
    loadAWSAccounts();
    loadEvidenceHistory();
    loadLicenseStatus();
    loadFrameworksFromAPI();
  }, []);

  const loadFrameworksFromAPI = async () => {
    setFrameworksLoading(true);
    try {
      // Use /my-frameworks: admins get all, users get only assigned ones
      const res = await axios.get('/api/regulatory-bot/my-frameworks');
      const fws = (res.data.frameworks || []).map(f => ({
        id: f.framework_id,
        name: f.name,
        description: f.description || '',
        icon: (FRAMEWORK_UI_META[f.framework_id] || DEFAULT_META).icon,
        color: f.icon_color || (FRAMEWORK_UI_META[f.framework_id] || DEFAULT_META).color,
        category: f.category || (FRAMEWORK_UI_META[f.framework_id] || DEFAULT_META).industry || 'Technology',
        total_checks: f.total_checks || 0,
        version: f.current_version || f.version || '',
      }));
      setComplianceFrameworks(fws);
    } catch (err) {
      console.error('Failed to load frameworks from API:', err);
    } finally {
      setFrameworksLoading(false);
    }
  };

  const loadLicenseStatus = async () => {
    setLicenseLoading(true);
    try {
      const response = await axios.get('/api/regulatory-bot/license-status');
      setLicenseStatus(response.data);
    } catch (err) {
      console.error('Failed to load license status:', err);
      setLicenseStatus({ valid: false, error: 'LICENSE_CHECK_ERROR', message: 'Unable to verify license' });
    } finally {
      setLicenseLoading(false);
    }
  };

  const loadAWSAccounts = async () => {
    try {
      const response = await axios.get('/api/aws/accounts');
      setAwsAccounts(response.data.accounts || []);
      if (response.data.accounts && response.data.accounts.length > 0) {
        setSelectedAccount(response.data.accounts[0].id);
      }
    } catch (err) {
      console.error('Failed to load AWS accounts:', err);
      showNotification('Failed to load AWS accounts', 'error');
    }
  };

  const loadEvidenceHistory = async () => {
    try {
      const response = await axios.get('/api/regulatory-bot/evidence/history');
      if (response.data.success) {
        setEvidenceHistory(response.data.history || []);
      }
    } catch (err) {
      console.error('Failed to load evidence history:', err);
    }
  };

  const loadFrameworkHistory = async (frameworkId) => {
    try {
      // Use the server-side filtered endpoint — avoids fetching all history just to filter client-side
      const response = await axios.get(`/api/regulatory-bot/evidence/history/framework/${frameworkId}`);
      if (response.data.success) {
        setFrameworkHistory(response.data.history || []);
      }
    } catch (err) {
      console.error('Failed to load framework history:', err);
    }
  };

  // --- Evidence Document Functions ---
  const loadBucketConfig = async () => {
    if (!selectedAccount) return;
    setEvidenceBucketLoading(true);
    try {
      const response = await axios.get(`/api/evidence-documents/bucket-config/${selectedAccount}`);
      const name = response.data.bucketName || '';
      setEvidenceBucket(name);
      setEvidenceBucketInput(name);
    } catch (err) {
      console.error('Failed to load bucket config:', err);
      setEvidenceBucket('');
      setEvidenceBucketInput('');
    } finally {
      setEvidenceBucketLoading(false);
    }
  };

  const handleSaveBucket = async () => {
    if (!evidenceBucketInput.trim()) return;
    setEvidenceBucketSaving(true);
    try {
      await axios.put(`/api/evidence-documents/bucket-config/${selectedAccount}`, {
        bucketName: evidenceBucketInput.trim()
      });
      setEvidenceBucket(evidenceBucketInput.trim());
      showNotification('S3 bucket configured successfully', 'success');
    } catch (err) {
      console.error('Failed to save bucket config:', err);
      showNotification(err.response?.data?.error || 'Failed to save bucket name', 'error');
    } finally {
      setEvidenceBucketSaving(false);
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      loadBucketConfig();
    }
  }, [selectedAccount]);

  const loadEvidenceDocs = async () => {
    if (!selectedFramework || !selectedAccount) return;
    try {
      const response = await axios.get(`/api/evidence-documents/${selectedAccount}/${selectedFramework.id}`);
      setEvidenceDocs(response.data.documents || {});
    } catch (err) {
      console.error('Failed to load evidence documents:', err);
    }
  };

  useEffect(() => {
    if (selectedFramework && selectedAccount) {
      loadEvidenceDocs();
    }
  }, [selectedFramework, selectedAccount]);

  const handleLocalUpload = (checkId) => {
    setPendingUploadCheckId(checkId);
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingUploadCheckId) return;

    setUploadingDoc(pendingUploadCheckId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('awsAccountId', selectedAccount);
      formData.append('frameworkId', selectedFramework.id);
      formData.append('checkId', pendingUploadCheckId);

      await axios.post('/api/evidence-documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      showNotification(`Uploaded "${file.name}" successfully`, 'success');
      await loadEvidenceDocs();
    } catch (err) {
      console.error('Upload failed:', err);
      showNotification('Failed to upload document: ' + (err.response?.data?.details || err.message), 'error');
    } finally {
      setUploadingDoc(null);
      setPendingUploadCheckId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleBrowseS3 = async (checkId) => {
    setS3BrowseDialog({ open: true, checkId, files: [], loading: true, error: null });
    try {
      const response = await axios.get(`/api/evidence-documents/browse-s3/${selectedAccount}/${selectedFramework.id}/${checkId}`);
      setS3BrowseDialog(prev => ({ ...prev, files: response.data.files || [], loading: false, error: null }));
    } catch (err) {
      console.error('S3 browse failed:', err);
      const errMsg = err.response?.data?.message || err.response?.data?.details || err.message || 'Could not browse S3 bucket';
      setS3BrowseDialog(prev => ({ ...prev, files: [], loading: false, error: errMsg }));
    }
  };

  const handleAttachS3File = async (s3Key) => {
    const checkId = s3BrowseDialog.checkId;
    setS3BrowseDialog({ open: false, checkId: null, files: [], loading: false, error: null });
    setUploadingDoc(checkId);
    try {
      await axios.post('/api/evidence-documents/attach-s3', {
        awsAccountId: selectedAccount,
        frameworkId: selectedFramework.id,
        checkId,
        s3Key
      });
      showNotification('S3 document attached successfully', 'success');
      await loadEvidenceDocs();
    } catch (err) {
      console.error('S3 attach failed:', err);
      showNotification('Failed to attach S3 document: ' + (err.response?.data?.details || err.message), 'error');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDownloadDoc = async (checkId) => {
    try {
      const response = await axios.get(`/api/evidence-documents/download/${selectedAccount}/${selectedFramework.id}/${checkId}`);
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (err) {
      console.error('Download failed:', err);
      showNotification('Failed to download document', 'error');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  // --- End Evidence Document Functions ---

  const handleDownloadChecks = async (frameworkId) => {
    setDownloadingChecks(true);
    setSyncStatus({
      step: 0,
      steps: [
        { label: 'Validating license', status: 'active' },
        { label: 'Connecting to control plane', status: 'pending' },
        { label: 'Fetching framework checks', status: 'pending' },
        { label: 'Updating local checklist', status: 'pending' }
      ],
      error: null,
      result: null
    });

    try {
      // Step 1: License validation (visual step — actual validation happens server-side)
      await new Promise(r => setTimeout(r, 400));
      setSyncStatus(prev => ({
        ...prev,
        step: 1,
        steps: prev.steps.map((s, i) =>
          i === 0 ? { ...s, status: 'done' } : i === 1 ? { ...s, status: 'active' } : s
        )
      }));

      // Step 2: Connecting (the POST triggers connection + fetch)
      await new Promise(r => setTimeout(r, 300));
      setSyncStatus(prev => ({
        ...prev,
        step: 2,
        steps: prev.steps.map((s, i) =>
          i <= 1 ? { ...s, status: 'done' } : i === 2 ? { ...s, status: 'active' } : s
        )
      }));

      const response = await axios.post(`/api/regulatory-bot/download/${frameworkId}`);

      // Step 3 & 4: Done
      setSyncStatus(prev => ({
        ...prev,
        step: 4,
        steps: prev.steps.map(s => ({ ...s, status: 'done' })),
        result: response.data.syncDetails || response.data
      }));

      const syncDetails = response.data.syncDetails || {};
      setLastSynced({
        frameworkId,
        time: syncDetails.syncedAt || new Date().toISOString(),
        version: syncDetails.version || response.data.version,
        checksCount: syncDetails.checksCount || response.data.checksCount
      });

      if (response.data.success) {
        const msg = response.data.skipped
          ? `Checks already up to date (v${response.data.version})`
          : `Downloaded ${response.data.checksCount} checks from ${response.data.source}`;
        showNotification(msg, response.data.skipped ? 'info' : 'success');

        if (!response.data.skipped) {
          const checklistResponse = await axios.get(`/api/regulatory-bot/checklist/${frameworkId}`);
          const checklistData = checklistResponse.data.checklist || [];
          setChecklist(checklistData.map(check => ({
            ...check,
            evidence: '', owner: '', comments: '', status: 'PENDING',
            affectedResources: '', remediationSteps: '', cliCommands: ''
          })));
        }
      }

      // Auto-dismiss sync status after 5 seconds on success
      setTimeout(() => setSyncStatus(null), 5000);
    } catch (err) {
      console.error('Failed to download checks:', err);
      const errorData = err.response?.data;
      const errorCode = errorData?.error || '';

      // Mark the failed step
      setSyncStatus(prev => {
        const failStep = errorCode.startsWith('LICENSE_') || errorCode === 'LICENSE_MISSING' ? 0
          : errorCode === 'CONTROL_PLANE_UNREACHABLE' ? 1
          : 2;
        return {
          ...prev,
          error: errorData?.message || 'Sync failed',
          steps: prev.steps.map((s, i) =>
            i < failStep ? { ...s, status: 'done' }
            : i === failStep ? { ...s, status: 'error' }
            : { ...s, status: 'pending' }
          )
        };
      });

      // Handle license-specific errors with clear messaging
      if (errorCode === 'LICENSE_MISSING' || errorCode === 'LICENSE_REQUIRED') {
        showNotification('License key not configured. Contact your administrator.', 'error');
        setError('No license key configured. Compliance features require a valid subscription.');
      } else if (errorCode === 'LICENSE_INVALID' || errorCode === 'LICENSE_AUTH_FAILED') {
        showNotification('Invalid license key. Contact your administrator.', 'error');
        setError('Invalid license key. Please check your subscription or contact support.');
      } else if (errorCode === 'LICENSE_EXPIRED') {
        showNotification('Your license has expired. Please renew your subscription.', 'error');
        setError('License expired. Renew your subscription to continue using compliance features.');
      } else if (errorCode === 'LICENSE_SUSPENDED' || errorCode === 'LICENSE_REVOKED' || errorCode === 'TENANT_SUSPENDED') {
        showNotification('Your license has been suspended. Contact support.', 'error');
        setError('License suspended. Contact support for assistance.');
      } else if (errorCode === 'FRAMEWORK_NOT_LICENSED') {
        showNotification(`Your plan does not include this framework. Upgrade to access it.`, 'warning');
        setError(errorData?.message || 'Framework not included in your current plan.');
      } else if (errorCode === 'RATE_LIMIT' || errorCode === 'RATE_LIMIT_EXCEEDED' || errorCode === 'DAILY_SYNC_LIMIT') {
        showNotification(errorData?.message || 'Rate limit exceeded. Try again later.', 'warning');
      } else {
        showNotification('Failed to sync compliance checks', 'error');
      }

      if (errorCode.startsWith('LICENSE_') || errorCode === 'TENANT_SUSPENDED') {
        loadLicenseStatus();
      }
    } finally {
      setDownloadingChecks(false);
    }
  };

  const loadChecklistUploadInfo = async (frameworkId) => {
    if (!isAdmin) return; // only admins see upload info
    try {
      const response = await axios.get(`/api/regulatory-bot/checklist-upload-info/${frameworkId}`);
      setChecklistUploads(response.data.uploads || []);
    } catch (err) {
      console.error('Failed to load checklist upload info:', err);
      setChecklistUploads([]);
    }
  };

  const handleUploadChecklist = async () => {
    if (!uploadChecklistFiles.length || !selectedFramework) return;
    setUploadChecklistLoading(true);
    setUploadChecklistResult(null);
    setUploadElapsedSecs(0);
    const timer = setInterval(() => setUploadElapsedSecs(s => s + 1), 1000);
    try {
      const formData = new FormData();
      uploadChecklistFiles.forEach(f => formData.append('file', f));

      // Submit all files at once — backend returns uploadIds array
      // No axios timeout — large files (90MB+) can take time to transfer
      const response = await axios.post(
        `/api/regulatory-bot/admin/upload-checklist/${selectedFramework.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
      );
      const uploadIds = response.data.uploadIds || [response.data.uploadId];

      // Poll all uploads until all complete or fail
      const maxAttempts = 450;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(r => setTimeout(r, 8000));
        const statuses = await Promise.all(
          uploadIds.map(id => axios.get(`/api/regulatory-bot/admin/upload-status/${id}`, { timeout: 30000 }).then(r => r.data))
        );
        const allDone = statuses.every(s => s.status === 'completed' || s.status === 'failed');
        if (allDone) {
          const completed = statuses.filter(s => s.status === 'completed');
          const failed = statuses.filter(s => s.status === 'failed');
          const totalParsed = completed.reduce((sum, s) => sum + (s.checksCount || 0), 0);
          const totalChecks = statuses[statuses.length - 1]?.totalChecks || 0;
          if (completed.length > 0) {
            setUploadChecklistResult({ success: true, checksCount: totalParsed, totalChecks, failedCount: failed.length });
            showNotification(`${totalParsed} checks parsed from ${completed.length} file(s) — ${totalChecks} total active`, 'success');
            const checklistResponse = await axios.get(`/api/regulatory-bot/checklist/${selectedFramework.id}`);
            const checklistData = checklistResponse.data.checklist || [];
            setChecklist(checklistData.map(check => ({
              ...check, evidence: '', owner: '', comments: '', status: 'PENDING',
              affectedResources: '', remediationSteps: '', cliCommands: ''
            })));
            setLastSynced({ frameworkId: selectedFramework.id, time: new Date().toISOString(), checksCount: totalChecks });
            await loadChecklistUploadInfo(selectedFramework.id);
          } else {
            const errMsg = failed[0]?.error || 'AI parsing failed for all files';
            setUploadChecklistResult({ success: false, message: errMsg });
            showNotification(errMsg, 'error');
          }
          return;
        }
      }
      setUploadChecklistResult({ success: false, message: 'Timed out after 60 min. The job may still be running — refresh in a few minutes.' });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Upload failed';
      setUploadChecklistResult({ success: false, message: msg });
      showNotification(msg, 'error');
    } finally {
      clearInterval(timer);
      setUploadChecklistLoading(false);
    }
  };

  // ── Framework assignment helpers (admin only) ─────────────────────────────
  const openAssignDialog = async () => {
    setAssignDialog(true);
    setAssignSelectedUser('');
    setAssignUserFrameworks([]);
    try {
      const res = await axios.get('/api/regulatory-bot/admin/users-list');
      setAssignUsers(res.data.users || []);
    } catch (err) {
      showNotification('Failed to load users', 'error');
    }
  };

  // ── Framework add/delete helpers (admin only) ─────────────────────────────
  const handleAddFramework = async () => {
    const { framework_id, name } = addFrameworkForm;
    if (!framework_id.trim() || !name.trim()) {
      showNotification('Framework ID and name are required', 'error');
      return;
    }
    setAddFrameworkSaving(true);
    try {
      await axios.post('/api/regulatory-bot/admin/frameworks', addFrameworkForm);
      showNotification(`Framework "${name}" added successfully`, 'success');
      setAddFrameworkDialog(false);
      setAddFrameworkForm({ framework_id: '', name: '', description: '', official_source_url: '' });
      const res = await axios.get('/api/regulatory-bot/my-frameworks');
      const raw = res.data.frameworks || [];
      setComplianceFrameworks(raw.map(fw => ({
        ...fw,
        id: fw.framework_id,
        icon: (FRAMEWORK_UI_META[fw.framework_id] || DEFAULT_META).icon,
        color: fw.icon_color || (FRAMEWORK_UI_META[fw.framework_id] || DEFAULT_META).color,
        category: fw.category || (FRAMEWORK_UI_META[fw.framework_id] || DEFAULT_META).industry || 'Technology',
      })));
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      showNotification(`Failed to add framework: ${msg}`, 'error');
    } finally {
      setAddFrameworkSaving(false);
    }
  };

  const handleDeleteFramework = async () => {
    const fw = deleteFrameworkDialog.framework;
    if (!fw) return;
    setDeleteFrameworkSaving(true);
    try {
      await axios.delete(`/api/regulatory-bot/admin/frameworks/${fw.id}`);
      showNotification(`Framework "${fw.name}" deleted`, 'info');
      setDeleteFrameworkDialog({ open: false, framework: null });
      setComplianceFrameworks(prev => prev.filter(f => f.id !== fw.id));
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      showNotification(`Failed to delete framework: ${msg}`, 'error');
    } finally {
      setDeleteFrameworkSaving(false);
    }
  };

  const loadUserAssignments = async (userId) => {
    setAssignSelectedUser(userId);
    try {
      const res = await axios.get(`/api/regulatory-bot/admin/framework-assignments/${userId}`);
      setAssignUserFrameworks((res.data.assignments || []).filter(a => a.is_active).map(a => a.framework_id));
    } catch (err) {
      showNotification('Failed to load user assignments', 'error');
    }
  };

  const handleToggleFrameworkForUser = async (frameworkId) => {
    if (!assignSelectedUser) return;
    const isAssigned = assignUserFrameworks.includes(frameworkId);
    setAssignSaving(true);
    try {
      if (isAssigned) {
        await axios.delete(`/api/regulatory-bot/admin/framework-assignments/${assignSelectedUser}/${frameworkId}`);
        setAssignUserFrameworks(prev => prev.filter(f => f !== frameworkId));
        showNotification('Framework access revoked', 'info');
      } else {
        await axios.post('/api/regulatory-bot/admin/framework-assignments', {
          userId: assignSelectedUser,
          frameworkIds: [frameworkId]
        });
        setAssignUserFrameworks(prev => [...prev, frameworkId]);
        showNotification('Framework assigned', 'success');
      }
    } catch (err) {
      const detail = err.response?.data?.details || err.response?.data?.error || err.message;
      showNotification(`Failed to update assignment: ${detail}`, 'error');
    } finally {
      setAssignSaving(false);
    }
  };

  const openAddCheck = () => {
    setCheckForm({ title: '', description: '', severity: 'MEDIUM', category: '', aws_service: '', requirement_section: '', official_reference: '' });
    setCheckDialog({ open: true, mode: 'add', check: null });
  };

  const openEditCheck = (check) => {
    setCheckForm({
      title: check.title || '',
      description: check.description || '',
      severity: check.severity || 'MEDIUM',
      category: check.category || '',
      aws_service: check.aws_service || '',
      requirement_section: check.requirement_section || '',
      official_reference: check.official_reference || ''
    });
    setCheckDialog({ open: true, mode: 'edit', check });
  };

  const handleSaveCheck = async () => {
    if (!checkForm.title.trim()) { showNotification('Title is required', 'warning'); return; }
    setCheckSaving(true);
    try {
      if (checkDialog.mode === 'add') {
        const res = await axios.post(`/api/regulatory-bot/admin/checks/${selectedFramework.id}`, checkForm);
        setChecklist(prev => [...prev, { ...res.data.check, evidence: '', owner: '', comments: '', status: 'PENDING', affectedResources: '', remediationSteps: '', cliCommands: '' }]);
        showNotification('Check added', 'success');
      } else {
        const res = await axios.put(`/api/regulatory-bot/admin/checks/${selectedFramework.id}/${checkDialog.check.check_id}`, checkForm);
        setChecklist(prev => prev.map(c => c.check_id === checkDialog.check.check_id ? { ...c, ...res.data.check } : c));
        showNotification('Check updated', 'success');
      }
      setCheckDialog({ open: false, mode: 'add', check: null });
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to save check', 'error');
    } finally {
      setCheckSaving(false);
    }
  };

  const handleDeleteUpload = async (upload) => {
    if (!window.confirm(`Remove "${upload.file_name}" and its ${upload.parsed_checks_count} checks? Manually added checks will be preserved.`)) return;
    try {
      const res = await axios.delete(`/api/regulatory-bot/admin/checklist-upload/${upload.id}`);
      setChecklistUploads(prev => prev.filter(u => u.id !== upload.id));
      // Reload checklist to reflect removed checks
      const checklistResponse = await axios.get(`/api/regulatory-bot/checklist/${selectedFramework.id}`);
      const checklistData = checklistResponse.data.checklist || [];
      setChecklist(checklistData.map(c => ({ ...c, evidence: '', owner: '', comments: '', status: 'PENDING', affectedResources: '', remediationSteps: '', cliCommands: '' })));
      showNotification(`Removed "${upload.file_name}" — ${res.data.checksRemoved} checks deleted`, 'success');
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to remove document', 'error');
    }
  };

  const handleDeleteCheck = async (check) => {
    if (!window.confirm(`Delete check "${check.title}"?`)) return;    try {
      await axios.delete(`/api/regulatory-bot/admin/checks/${selectedFramework.id}/${check.check_id}`);
      setChecklist(prev => prev.filter(c => c.check_id !== check.check_id));
      showNotification('Check deleted', 'success');
    } catch (err) {
      showNotification(err.response?.data?.error || 'Failed to delete check', 'error');
    }
  };

  const handleFrameworkSelect = async (framework) => {
    setSelectedFramework(framework);
    setExpandedFramework('');
    setError(null);
    setChecklistUploads([]);
    setChecklist([]); // clear immediately so UI doesn't show stale data
    setChecklistPage(0); // reset pagination

    // Run all independent fetches in parallel
    const [checklistRes, historyRes, uploadInfoRes, latestEvidenceRes] = await Promise.allSettled([
      axios.get(`/api/regulatory-bot/checklist/${framework.id}`),
      axios.get(`/api/regulatory-bot/evidence/history/framework/${framework.id}`),
      isAdmin ? axios.get(`/api/regulatory-bot/checklist-upload-info/${framework.id}`) : Promise.resolve(null),
      selectedAccount
        ? axios.get(`/api/regulatory-bot/evidence/latest/${framework.id}/${selectedAccount}`)
        : Promise.resolve(null)
    ]);

    // 1. Framework history
    if (historyRes.status === 'fulfilled' && historyRes.value?.data?.success) {
      setFrameworkHistory(historyRes.value.data.history || []);
    }

    // 2. Admin upload info
    if (isAdmin && uploadInfoRes.status === 'fulfilled' && uploadInfoRes.value?.data) {
      setChecklistUploads(uploadInfoRes.value.data.uploads || []);
    }

    // 3. Checklist — merge with latest evidence if available
    if (checklistRes.status === 'fulfilled') {
      const checklistData = checklistRes.value.data.checklist || [];
      const latestEvidence = latestEvidenceRes?.status === 'fulfilled' && latestEvidenceRes.value?.data?.hasEvidence
        ? (Array.isArray(latestEvidenceRes.value.data.evidence) ? latestEvidenceRes.value.data.evidence : [])
        : [];

      const initializedChecklist = checklistData.map(check => {
        const ev = latestEvidence.find(e => e.checkId === check.id);
        return {
          ...check,
          evidence: ev?.aiAnalysis || '',
          owner: ev?.owner || '',
          comments: ev?.comments || '',
          status: ev?.status || 'PENDING',
          resourcesAssessed: ev?.resourcesAssessed || '',
          affectedResources: ev?.affectedResources || '',
          recommendation: ev?.recommendation || '',
          remediationSteps: Array.isArray(ev?.remediationSteps)
            ? ev.remediationSteps.join('\n') : (ev?.remediationSteps || ''),
          cliCommands: Array.isArray(ev?.cliCommands)
            ? ev.cliCommands.join('\n') : (ev?.cliCommands || ''),
          downtimeRequired: ev?.downtimeRequired || ''
        };
      });

      setChecklist(initializedChecklist);
      if (latestEvidence.length > 0) showNotification('Loaded previous evidence', 'info');
    } else {
      setError('Failed to load framework checklist');
      showNotification('Failed to load framework', 'error');
    }
  };

  const handleGenerateEvidence = async () => {
    if (licenseStatus && !licenseStatus.valid) {
      showNotification('Valid license required to generate evidence', 'error');
      return;
    }
    if (!selectedAccount) {
      showNotification('Please select an AWS account', 'warning');
      return;
    }
    if (checklist.length === 0) {
      showNotification('Please select a framework first', 'warning');
      return;
    }

    setGeneratingEvidence(true);
    setError(null);

    // Mark all checks as pending so the UI shows a spinner state
    setChecklist(prev => prev.map(c => ({ ...c, status: 'PENDING', evidence: '', affectedResources: '', remediationSteps: '', cliCommands: '' })));

    try {
      // Get auth token for the SSE request header (EventSource doesn't support headers,
      // so we use fetch with a ReadableStream instead)
      const token = localStorage.getItem('token') || sessionStorage.getItem('token') || '';

      const response = await fetch('/api/regulatory-bot/generate-evidence', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          frameworkId: selectedFramework.id,
          accountId: selectedAccount,
          checklist,
          modelId: 'us.anthropic.claude-sonnet-4-5-20250929-v1:0'
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const parseSSE = (raw) => {
        // SSE format: "event: <name>\ndata: <json>\n\n"
        const events = [];
        const blocks = raw.split('\n\n');
        for (const block of blocks) {
          if (!block.trim()) continue;
          const lines = block.split('\n');
          let eventName = 'message', dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) eventName = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr = line.slice(6).trim();
          }
          if (dataStr) {
            try { events.push({ event: eventName, data: JSON.parse(dataStr) }); } catch (_) {}
          }
        }
        return events;
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Process all complete SSE blocks (terminated by \n\n)
        const lastDouble = buffer.lastIndexOf('\n\n');
        if (lastDouble === -1) continue;
        const toProcess = buffer.slice(0, lastDouble + 2);
        buffer = buffer.slice(lastDouble + 2);

        for (const { event, data } of parseSSE(toProcess)) {
          if (event === 'error') {
            setError(data.error || 'Failed to generate evidence');
            showNotification(data.error || 'Failed to generate evidence', 'error');
            setGeneratingEvidence(false);
            return;
          }

          if (event === 'check') {
            // Update the specific check in the checklist as it arrives
            const ev = data.evidence;
            setChecklist(prev => prev.map(c => {
              if ((c.id || c.check_id) !== ev.checkId) return c;
              return {
                ...c,
                evidence: ev.aiAnalysis || '',
                status: ev.status || 'PENDING',
                resourcesAssessed: ev.resourcesAssessed || '',
                affectedResources: ev.affectedResources || 'Not specified',
                recommendation: ev.recommendation || '',
                remediationSteps: Array.isArray(ev.remediationSteps)
                  ? ev.remediationSteps.join('\n') : (ev.remediationSteps || ''),
                cliCommands: Array.isArray(ev.cliCommands)
                  ? ev.cliCommands.join('\n') : (ev.cliCommands || ''),
                downtimeRequired: ev.downtimeRequired || ''
              };
            }));
          }

          if (event === 'done') {
            showNotification(
              `Evidence generated — ${data.summary.passed} passed, ${data.summary.failed} failed`,
              data.summary.failed > 0 ? 'warning' : 'success'
            );
            loadFrameworkHistory(selectedFramework.id);
            loadEvidenceHistory();
            setGeneratingEvidence(false);
          }
        }
      }
    } catch (err) {
      console.error('Failed to generate evidence:', err);
      setError(err.message || 'Failed to generate evidence');
      showNotification(err.message || 'Failed to generate evidence', 'error');
    } finally {
      setGeneratingEvidence(false);
    }
  };

  const handleUpdateCheck = (index, field, value) => {
    const updatedChecklist = [...checklist];
    updatedChecklist[index][field] = value;
    setChecklist(updatedChecklist);
  };

  const handleDownloadChecklist = () => {
    // Prepare data for Excel
    const data = checklist.map((check, index) => ({
      '#': index + 1,
      'Check Name': check.title,
      'Category': check.category,
      'Severity': check.severity,
      'Evidence': check.evidence || '',
      'Affected Resources': check.affectedResources || '',
      'Owner': check.owner || '',
      'Comments': check.comments || '',
      'Status': check.status || 'PENDING',
      'Remediation Steps': check.remediationSteps || '',
      'CLI Commands': check.cliCommands || ''
    }));

    // Convert to CSV for now (Excel export requires additional library)
    const csvContent = [
      Object.keys(data[0]).join(','),
      ...data.map(row => 
        Object.values(row).map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFramework.id}-evidence-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    
    showNotification('Checklist downloaded as CSV', 'success');
  };

  const handleDownloadExcel = () => {
    // Prepare data for Excel with better formatting
    const data = checklist.map((check, index) => ({
      '#': index + 1,
      'Check Name': check.title,
      'Category': check.category,
      'Severity': check.severity,
      'Evidence': check.evidence || '',
      'Affected Resources': check.affectedResources || '',
      'Owner': check.owner || '',
      'Comments': check.comments || '',
      'Status': check.status || 'PENDING',
      'Remediation Steps': check.remediationSteps || '',
      'CLI Commands': check.cliCommands || ''
    }));

    // Create Excel-compatible format (TSV - Tab Separated Values)
    // This opens properly in Excel with better formatting than CSV
    const tsvContent = [
      Object.keys(data[0]).join('\t'),
      ...data.map(row => 
        Object.values(row).map(cell => String(cell).replace(/\t/g, ' ')).join('\t')
      )
    ].join('\n');

    const blob = new Blob([tsvContent], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedFramework.id}-evidence-${Date.now()}.xls`;
    a.click();
    URL.revokeObjectURL(url);
    
    showNotification('Checklist downloaded as Excel', 'success');
  };

  const handleLoadHistoricalEvidence = async (evidenceId) => {
    try {
      const response = await axios.get(`/api/regulatory-bot/evidence/${evidenceId}`);
      const data = response.data;
      
      // Find framework
      const framework = complianceFrameworks.find(f => f.id === data.frameworkId);
      if (framework) {
        setSelectedFramework(framework);
      }
      
      setSelectedAccount(data.accountId);
      
      // Load checklist and merge with evidence
      const checklistResponse = await axios.get(`/api/regulatory-bot/checklist/${data.frameworkId}`);
      const checklistData = checklistResponse.data.checklist || [];
      
      // Evidence is already parsed by backend
      const evidenceArray = Array.isArray(data.evidence) ? data.evidence : [];
      
      const mergedChecklist = checklistData.map(check => {
        const evidence = evidenceArray.find(e => e.checkId === check.id);
        return {
          ...check,
          evidence: evidence?.aiAnalysis || '',
          owner: evidence?.owner || '',
          comments: evidence?.comments || '',
          status: evidence?.status || 'PENDING',
          resourcesAssessed: evidence?.resourcesAssessed || '',
          affectedResources: evidence?.affectedResources || '',
          recommendation: evidence?.recommendation || '',
          remediationSteps: Array.isArray(evidence?.remediationSteps)
            ? evidence.remediationSteps.join('\n')
            : (evidence?.remediationSteps || ''),
          cliCommands: Array.isArray(evidence?.cliCommands)
            ? evidence.cliCommands.join('\n')
            : (evidence?.cliCommands || ''),
          downtimeRequired: evidence?.downtimeRequired || ''
        };
      });
      
      setChecklist(mergedChecklist);
      setShowHistory(false);
      showNotification('Loaded historical evidence', 'info');
    } catch (err) {
      console.error('Failed to load historical evidence:', err);
      showNotification('Failed to load historical evidence', 'error');
    }
  };

  const handleDeleteEvidence = async (evidenceId, event) => {
    event.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this evidence?')) {
      try {
        await axios.delete(`/api/regulatory-bot/evidence/${evidenceId}`);
        loadEvidenceHistory();
        showNotification('Evidence deleted successfully', 'success');
      } catch (err) {
        console.error('Failed to delete evidence:', err);
        showNotification('Failed to delete evidence', 'error');
      }
    }
  };

  const handleClearHistory = async () => {
    if (window.confirm('Are you sure you want to clear all evidence history? This cannot be undone.')) {
      try {
        await axios.delete('/api/regulatory-bot/evidence/history/all');
        setEvidenceHistory([]);
        showNotification('Evidence history cleared', 'success');
      } catch (err) {
        console.error('Failed to clear history:', err);
        showNotification('Failed to clear history', 'error');
      }
    }
  };

  const handleReset = () => {
    setSelectedFramework(null);
    setChecklist([]);
    setError(null);
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return '#d32f2f';
      case 'HIGH': return '#f57c00';
      case 'MEDIUM': return '#fbc02d';
      case 'LOW': return '#388e3c';
      default: return '#757575';
    }
  };

  const getStatusIcon = (status, title) => {
    const handleClick = () => {
      setStatusDialog({ open: true, status, title });
    };

    const iconProps = {
      onClick: handleClick,
      sx: { cursor: 'pointer', '&:hover': { opacity: 0.7 } }
    };

    switch (status) {
      case 'PASS': return <CheckCircle {...iconProps} sx={{ ...iconProps.sx, color: '#4caf50' }} />;
      case 'FAIL': return <ErrorIcon {...iconProps} sx={{ ...iconProps.sx, color: '#f44336' }} />;
      case 'WARNING': return <Warning {...iconProps} sx={{ ...iconProps.sx, color: '#ff9800' }} />;
      case 'ERROR': return <ErrorIcon {...iconProps} sx={{ ...iconProps.sx, color: '#d32f2f' }} />;
      default: return <CheckBoxOutlineBlank {...iconProps} sx={{ ...iconProps.sx, color: '#9e9e9e' }} />;
    }
  };

  const getStatusExplanation = (status) => {
    switch (status) {
      case 'PASS':
        return {
          title: 'Compliant',
          color: '#4caf50',
          icon: <CheckCircle sx={{ fontSize: 48, color: '#4caf50' }} />,
          description: 'This control has been assessed and all resources are compliant with the requirement. No action needed.'
        };
      case 'FAIL':
        return {
          title: 'Non-Compliant',
          color: '#f44336',
          icon: <ErrorIcon sx={{ fontSize: 48, color: '#f44336' }} />,
          description: 'This control has failed compliance assessment. One or more resources do not meet the requirement. Immediate remediation is recommended.'
        };
      case 'WARNING':
        return {
          title: 'Warning',
          color: '#ff9800',
          icon: <Warning sx={{ fontSize: 48, color: '#ff9800' }} />,
          description: 'This control has potential compliance issues or could not be fully assessed. Review the findings and take appropriate action.'
        };
      case 'ERROR':
        return {
          title: 'Assessment Error',
          color: '#d32f2f',
          icon: <ErrorIcon sx={{ fontSize: 48, color: '#d32f2f' }} />,
          description: 'An error occurred while assessing this control. This may be due to insufficient permissions, network issues, or service unavailability. Check AWS credentials and try again.'
        };
      default:
        return {
          title: 'Pending',
          color: '#9e9e9e',
          icon: <CheckBoxOutlineBlank sx={{ fontSize: 48, color: '#9e9e9e' }} />,
          description: 'This control has not been assessed yet. Click "Generate Evidence with AI" to analyze compliance status.'
        };
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getFrameworkInfo = (frameworkId) => {
    return complianceFrameworks.find(f => f.id === frameworkId);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: '#7b1fa2', width: 56, height: 56 }}>
                <Assessment sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  Astra Compliance
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  AI-powered compliance evidence generation
                </Typography>
              </Box>
            </Box>
            
            {/* History Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* License Status Indicator — hidden for admins (always valid) */}
              {!isAdmin && !licenseLoading && licenseStatus && (
                <Chip
                  size="small"
                  icon={licenseStatus.valid ? <CheckCircle /> : <ErrorIcon />}
                  label={
                    licenseStatus.valid
                      ? `${licenseStatus.license?.tier?.toUpperCase()} License`
                      : (licenseStatus.error === 'LICENSE_MISSING' ? 'No License' : 'License Invalid')
                  }
                  color={licenseStatus.valid ? 'success' : 'error'}
                  variant="outlined"
                  sx={{ fontWeight: 500 }}
                />
              )}
              {!isAdmin && licenseLoading && (
                <Chip size="small" label="Checking license..." variant="outlined" />
              )}
              <Badge badgeContent={evidenceHistory.length} color="primary">
                <Button
                  variant="outlined"
                  startIcon={<History />}
                  onClick={() => setShowHistory(true)}
                >
                  Evidence History
                </Button>
              </Badge>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* License Error Banner — only shown to non-admin users when license is invalid */}
        {!isAdmin && !licenseLoading && licenseStatus && !licenseStatus.valid && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={loadLicenseStatus}>
                Retry
              </Button>
            }
          >
            {licenseStatus.error === 'LICENSE_MISSING'
              ? 'No license key configured. Compliance features require a valid subscription. Contact your administrator to set up a license.'
              : licenseStatus.error === 'LICENSE_EXPIRED'
              ? 'Your license has expired. Please renew your subscription to continue using compliance features.'
              : licenseStatus.message || 'License validation failed. Compliance features are unavailable.'}
          </Alert>
        )}

        {/* Framework Selection or Checklist */}
        {!selectedFramework ? (
          <Card sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assessment color="primary" />
                Select Compliance Framework
              </Typography>

              {/* Industry Filter Buttons */}
              <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {dynamicCategories.map((industry) => (
                  <Button
                    key={industry.id}
                    variant={selectedIndustry === industry.id ? 'contained' : 'outlined'}
                    startIcon={industry.icon}
                    onClick={() => setSelectedIndustry(industry.id)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      ...(selectedIndustry === industry.id && industry.color && {
                        bgcolor: industry.color,
                        '&:hover': {
                          bgcolor: industry.color,
                          filter: 'brightness(0.9)'
                        }
                      })
                    }}
                  >
                    {industry.name}
                  </Button>
                ))}
              </Box>

              {/* Framework Count Badge */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Chip 
                  label={`${filteredFrameworks.length} framework${filteredFrameworks.length !== 1 ? 's' : ''} available`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                {isAdmin && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Security />}
                      onClick={openAssignDialog}
                      sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Assign Frameworks to Users
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Add />}
                      onClick={() => {
                        setAddFrameworkForm({ framework_id: '', name: '', description: '', official_source_url: '' });
                        setAddFrameworkDialog(true);
                      }}
                      sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                    >
                      Add Framework
                    </Button>
                  </>
                )}
              </Box>

              <Grid container spacing={3}>
                {filteredFrameworks.map((framework) => (
                  <Grid item xs={12} sm={6} md={4} key={framework.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        border: '1px solid #e0e0e0',
                        height: '100%',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4,
                          borderColor: framework.color
                        }
                      }}
                      onClick={() => handleFrameworkSelect(framework)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ bgcolor: framework.color, width: 48, height: 48 }}>
                            <RBIcon name={framework.icon} />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                              {framework.name}
                            </Typography>
                          </Box>
                          {isAdmin && (
                            <Tooltip title="Delete framework">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteFrameworkDialog({ open: true, framework });
                                }}
                                sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.light', color: 'white' } }}
                              >
                                <DeleteOutline fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                          {framework.description}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {framework.total_checks > 0 && (
                            <Chip label={`${framework.total_checks} checks`} size="small" sx={{ fontSize: '0.7rem' }} />
                          )}
                          <Chip label={getFrameworkIndustry(framework.id)} size="small" sx={{ fontSize: '0.7rem' }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ) : (
          <Box>
            {/* Framework Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton onClick={handleReset} size="small" sx={{ bgcolor: 'action.hover' }}>
                    <ArrowBack />
                  </IconButton>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedFramework.name} Checklist
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFramework.description}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Framework-specific history button */}
                  <Badge badgeContent={frameworkHistory.length} color="primary">
                    <Button
                      variant="outlined"
                      startIcon={<History />}
                      onClick={() => setShowFrameworkHistory(true)}
                      size="small"
                    >
                      {selectedFramework.name} History
                    </Button>
                  </Badge>
                  
                  {/* Admin tools always visible so new frameworks can be populated */}
                  {isAdmin && (
                    <>
                      <Button
                        startIcon={<UploadFile />}
                        onClick={() => {
                          setUploadChecklistFiles([]);
                          setUploadChecklistResult(null);
                          setUploadChecklistDialog(true);
                        }}
                        variant="outlined"
                        size="small"
                        color="secondary"
                      >
                        Upload Checklist
                      </Button>
                      <Button
                        startIcon={<Add />}
                        onClick={openAddCheck}
                        variant="outlined"
                        size="small"
                        color="primary"
                      >
                        Add Check
                      </Button>
                    </>
                  )}
                  {lastSynced && lastSynced.frameworkId === selectedFramework.id && (
                    <Tooltip title={`v${lastSynced.version} · ${lastSynced.checksCount} checks · ${new Date(lastSynced.time).toLocaleString()}`}>
                      <Chip
                        size="small"
                        icon={<CheckCircle />}
                        label={`Synced ${formatRelativeTime(lastSynced.time)}`}
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Tooltip>
                  )}
                  {checklist.length > 0 && (
                    <>
                      <Button
                        startIcon={<Download />}
                        onClick={handleDownloadChecklist}
                        variant="outlined"
                        size="small"
                      >
                        Download CSV
                      </Button>
                      <Button
                        startIcon={<Download />}
                        onClick={handleDownloadExcel}
                        variant="contained"
                        size="small"
                        sx={{ bgcolor: '#217346', '&:hover': { bgcolor: '#1a5c37' } }}
                      >
                        Download Excel
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              {/* AWS Account Selector */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>AWS Account</InputLabel>
                    <Select
                      value={selectedAccount}
                      onChange={(e) => setSelectedAccount(e.target.value)}
                      label="AWS Account"
                    >
                      {awsAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.account_name} ({account.account_id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Checklist Source Documents Banner — admin only, shows all uploaded docs */}
            {isAdmin && checklistUploads.length > 0 && (
              <Paper sx={{
                p: 2, mb: 2,
                border: '1px solid #1565c0',
                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#0d2137' : '#e3f2fd'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <InsertDriveFile sx={{ color: '#1565c0', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#90caf9' : '#0d47a1' }}>
                    Checklist Sources ({checklistUploads.length} document{checklistUploads.length !== 1 ? 's' : ''})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<UploadFile sx={{ fontSize: 14 }} />}
                    onClick={() => { setUploadChecklistFiles([]); setUploadChecklistResult(null); setUploadChecklistDialog(true); }}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', ml: 'auto', borderColor: '#1565c0', color: '#1565c0' }}
                  >
                    Add Document
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {checklistUploads.map(upload => (
                    <Chip
                      key={upload.id}
                      label={`${upload.file_name} (${upload.parsed_checks_count} checks)`}
                      size="small"
                      icon={<CheckCircle sx={{ fontSize: 14, color: '#1565c0 !important' }} />}
                      onDelete={() => handleDeleteUpload(upload)}
                      deleteIcon={
                        <Tooltip title="Remove this document and its checks">
                          <DeleteOutline sx={{ fontSize: 16 }} />
                        </Tooltip>
                      }
                      title={`Uploaded ${formatRelativeTime(upload.created_at)} by ${upload.uploaded_by_email}`}
                      sx={{
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1a3a5c' : '#bbdefb',
                        color: (theme) => theme.palette.mode === 'dark' ? '#90caf9' : '#0d47a1',
                        border: '1px solid #1565c0',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        '& .MuiChip-deleteIcon': {
                          color: (theme) => theme.palette.mode === 'dark' ? '#ef9a9a' : '#c62828',
                          '&:hover': { color: '#f44336' }
                        }
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Sync Status Panel */}
            {syncStatus && (
              <Paper sx={{ p: 2, mb: 3, border: syncStatus.error ? '1px solid #f44336' : '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {syncStatus.error ? 'Sync Failed' : syncStatus.step >= 4 ? 'Sync Complete' : 'Syncing with Control Plane...'}
                  </Typography>
                  {(syncStatus.error || syncStatus.step >= 4) && (
                    <IconButton size="small" onClick={() => setSyncStatus(null)}>
                      <Typography sx={{ fontSize: '1rem', lineHeight: 1 }}>✕</Typography>
                    </IconButton>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  {syncStatus.steps.map((step, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {step.status === 'active' && <CircularProgress size={16} />}
                      {step.status === 'done' && <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />}
                      {step.status === 'error' && <ErrorIcon sx={{ fontSize: 18, color: '#f44336' }} />}
                      {step.status === 'pending' && <CheckBoxOutlineBlank sx={{ fontSize: 18, color: '#bdbdbd' }} />}
                      <Typography
                        variant="caption"
                        sx={{
                          color: step.status === 'active' ? 'primary.main'
                            : step.status === 'done' ? 'success.main'
                            : step.status === 'error' ? 'error.main'
                            : 'text.disabled',
                          fontWeight: step.status === 'active' ? 600 : 400
                        }}
                      >
                        {step.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {syncStatus.error && (
                  <Alert severity="error" sx={{ mt: 1.5 }} variant="outlined">
                    {syncStatus.error}
                  </Alert>
                )}
                {syncStatus.result && !syncStatus.error && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`v${syncStatus.result.version}`} variant="outlined" />
                    <Chip size="small" label={`${syncStatus.result.checksCount} checks`} variant="outlined" />
                    <Chip size="small" label={`${syncStatus.result.durationMs}ms`} variant="outlined" />
                    {syncStatus.result.licenseTier && (
                      <Chip size="small" label={`${syncStatus.result.licenseTier} tier`} color="primary" variant="outlined" />
                    )}
                  </Box>
                )}
              </Paper>
            )}

            {/* Checklist Table */}
            {checklist.length > 0 && (
              <>
                {/* S3 Bucket Configuration Bar */}
                <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Storage sx={{ color: evidenceBucket ? 'success.main' : 'warning.main', fontSize: 22 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 'fit-content' }}>
                    Evidence S3 Bucket:
                  </Typography>
                  {evidenceBucketLoading ? (
                    <CircularProgress size={20} />
                  ) : evidenceBucket ? (
                    <>
                      <Chip
                        label={evidenceBucket}
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<CheckCircle sx={{ fontSize: 16 }} />}
                      />
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setEvidenceBucket('')}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        Change
                      </Button>
                    </>
                  ) : (
                    <>
                      <TextField
                        size="small"
                        placeholder="e.g. my-company-compliance-bucket"
                        value={evidenceBucketInput}
                        onChange={(e) => setEvidenceBucketInput(e.target.value)}
                        sx={{ minWidth: 280 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveBucket(); }}
                      />
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveBucket}
                        disabled={!evidenceBucketInput.trim() || evidenceBucketSaving}
                        startIcon={evidenceBucketSaving ? <CircularProgress size={14} color="inherit" /> : null}
                        sx={{ textTransform: 'none' }}
                      >
                        {evidenceBucketSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </>
                  )}
                </Paper>

                <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 600, overflowX: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell width="3%" sx={{ minWidth: 40 }}>#</TableCell>
                        <TableCell width="15%" sx={{ minWidth: 160 }}>Check Name</TableCell>
                        <TableCell width="7%" sx={{ minWidth: 80 }}>Severity</TableCell>
                        <TableCell width="17%" sx={{ minWidth: 180 }}>Evidence</TableCell>
                        <TableCell width="12%" sx={{ minWidth: 130 }}>Affected Resources</TableCell>
                        <TableCell width="8%" sx={{ minWidth: 90 }}>Owner</TableCell>
                        <TableCell width="10%" sx={{ minWidth: 110 }}>Comments</TableCell>
                        <TableCell width="13%" sx={{ minWidth: 150 }}>Evidence Document</TableCell>
                        <TableCell width="10%" sx={{ minWidth: 100 }}>Remediation</TableCell>
                        <TableCell width="5%" sx={{ minWidth: 60 }}>Status</TableCell>
                        {isAdmin && <TableCell width="5%" sx={{ minWidth: 70 }}>Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        const sorted = [...checklist].sort((a, b) => {
                          const severityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
                          return (severityOrder[a.severity] ?? 4) - (severityOrder[b.severity] ?? 4);
                        });
                        const start = checklistPage * CHECKS_PER_PAGE;
                        const pageItems = sorted.slice(start, start + CHECKS_PER_PAGE);
                        return pageItems.map((item, idx) => {
                          const index = start + idx;
                          return (
                        <TableRow key={item.id} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{index + 1}</Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                              {item.id || item.check_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {item.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {item.category}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.severity}
                              size="small"
                              sx={{
                                bgcolor: getSeverityColor(item.severity),
                                color: 'white',
                                fontSize: '0.7rem',
                                fontWeight: 600
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box>
                              {/* Evidence Summary */}
                              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
                                {item.evidence ? formatEvidenceSummary(item) : 'AI will generate evidence here...'}
                              </Typography>
                              
                              {/* Resource Counts */}
                              {item.resourcesAssessed && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip 
                                    label={item.resourcesAssessed} 
                                    size="small" 
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      height: 20,
                                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'
                                    }} 
                                  />
                                  {getResourceCounts(item).passed > 0 && (
                                    <Chip 
                                      label={`✓ ${getResourceCounts(item).passed} passed`}
                                      size="small" 
                                      sx={{ 
                                        fontSize: '0.7rem',
                                        height: 20,
                                        bgcolor: 'rgba(76, 175, 80, 0.2)',
                                        color: 'success.main'
                                      }} 
                                    />
                                  )}
                                  {getResourceCounts(item).failed > 0 && (
                                    <Chip 
                                      label={`✗ ${getResourceCounts(item).failed} failed`}
                                      size="small" 
                                      sx={{ 
                                        fontSize: '0.7rem',
                                        height: 20,
                                        bgcolor: 'rgba(244, 67, 54, 0.2)',
                                        color: 'error.main'
                                      }} 
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ 
                              maxHeight: 120, 
                              overflowY: 'auto',
                              p: 1,
                              bgcolor: (theme) => 
                                item.affectedResources?.toLowerCase().includes('none') || 
                                item.affectedResources?.toLowerCase().includes('compliant')
                                  ? theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)'
                                  : theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                              borderRadius: 1,
                              border: (theme) => `1px solid ${
                                item.affectedResources?.toLowerCase().includes('none') || 
                                item.affectedResources?.toLowerCase().includes('compliant')
                                  ? theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'
                                  : theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'
                              }`
                            }}>
                              {formatAffectedResources(item.affectedResources)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              value={item.owner || ''}
                              onChange={(e) => handleUpdateCheck(index, 'owner', e.target.value)}
                              placeholder="Assign owner..."
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              multiline
                              rows={2}
                              size="small"
                              value={item.comments || ''}
                              onChange={(e) => handleUpdateCheck(index, 'comments', e.target.value)}
                              placeholder="Add comments..."
                              variant="outlined"
                            />
                          </TableCell>
                          {/* Evidence Document Column */}
                          <TableCell>
                            {!evidenceBucket ? (
                              <Box sx={{ opacity: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  Please configure S3 bucket above
                                </Typography>
                              </Box>
                            ) : (() => {
                              const doc = evidenceDocs[item.id];
                              const isUploading = uploadingDoc === item.id;
                              if (isUploading) {
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <Typography variant="caption">Uploading...</Typography>
                                  </Box>
                                );
                              }
                              if (doc) {
                                return (
                                  <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                      <InsertDriveFile sx={{ fontSize: 16, color: 'primary.main' }} />
                                      <Tooltip title={doc.fileName}>
                                        <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                                          {doc.fileName}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                    {doc.fileSize > 0 && (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        {formatFileSize(doc.fileSize)}
                                      </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                      <Tooltip title="Download">
                                        <IconButton size="small" onClick={() => handleDownloadDoc(item.id)} sx={{ p: 0.3 }}>
                                          <Download sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Re-upload from local">
                                        <IconButton size="small" onClick={() => handleLocalUpload(item.id)} sx={{ p: 0.3 }}>
                                          <CloudUpload sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Re-upload from S3">
                                        <IconButton size="small" onClick={() => handleBrowseS3(item.id)} sx={{ p: 0.3 }}>
                                          <Storage sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                );
                              }
                              return (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                    No document attached
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<CloudUpload sx={{ fontSize: 14 }} />}
                                      onClick={() => handleLocalUpload(item.id)}
                                      sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.2, px: 0.8 }}
                                    >
                                      Local
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Storage sx={{ fontSize: 14 }} />}
                                      onClick={() => handleBrowseS3(item.id)}
                                      sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.2, px: 0.8 }}
                                    >
                                      S3
                                    </Button>
                                  </Box>
                                </Box>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            {item.remediationSteps || item.cliCommands ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Build />}
                                onClick={() => setRemediationDialog({ open: true, check: item })}
                                sx={{ textTransform: 'none' }}
                              >
                                View Steps
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {item.status === 'PASS' ? 'Compliant' : 'Pending'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Click for details">
                              {getStatusIcon(item.status, item.title)}
                            </Tooltip>
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Edit check">
                                  <IconButton size="small" onClick={() => openEditCheck(item)} sx={{ p: 0.4 }}>
                                    <EditIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete check">
                                  <IconButton size="small" onClick={() => handleDeleteCheck(item)} sx={{ p: 0.4 }}>
                                    <DeleteOutline sx={{ fontSize: 16, color: 'error.main' }} />
                                  </IconButton>
                                </Tooltip>
                                {item.check_source === 'manual' && (
                                  <Chip label="manual" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: 'primary.50', color: 'primary.main' }} />
                                )}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination controls */}
                {checklist.length > CHECKS_PER_PAGE && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, px: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {checklistPage * CHECKS_PER_PAGE + 1}–{Math.min((checklistPage + 1) * CHECKS_PER_PAGE, checklist.length)} of {checklist.length} checks
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={checklistPage === 0}
                        onClick={() => setChecklistPage(p => p - 1)}
                        sx={{ textTransform: 'none', minWidth: 80 }}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={(checklistPage + 1) * CHECKS_PER_PAGE >= checklist.length}
                        onClick={() => setChecklistPage(p => p + 1)}
                        sx={{ textTransform: 'none', minWidth: 80 }}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Hidden file input for local uploads */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileSelected}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.zip"
                />

                {/* S3 Browse Dialog */}
                <Dialog
                  open={s3BrowseDialog.open}
                  onClose={() => setS3BrowseDialog({ open: false, checkId: null, files: [], loading: false, error: null })}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogTitle>Select File from S3 Bucket</DialogTitle>
                  <DialogContent>
                    {s3BrowseDialog.loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : s3BrowseDialog.error ? (
                      <Box sx={{ py: 2 }}>
                        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                          Failed to browse S3 bucket:
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {s3BrowseDialog.error}
                        </Typography>
                      </Box>
                    ) : s3BrowseDialog.files.length === 0 ? (
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        No files found in the S3 bucket for this check. Upload a file locally first, or place files in the bucket manually.
                      </Typography>
                    ) : (
                      <List>
                        {s3BrowseDialog.files.map((file) => (
                          <ListItem
                            key={file.key}
                            component="div"
                            onClick={() => handleAttachS3File(file.key)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                          >
                            <ListItemIcon>
                              <InsertDriveFile />
                            </ListItemIcon>
                            <ListItemText
                              primary={file.fileName}
                              secondary={`${formatFileSize(file.size)} • ${new Date(file.lastModified).toLocaleDateString()}`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setS3BrowseDialog({ open: false, checkId: null, files: [], loading: false, error: null })}>
                      Cancel
                    </Button>
                  </DialogActions>
                </Dialog>

                {/* Generate Evidence Button */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={generatingEvidence ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
                    onClick={handleGenerateEvidence}
                    disabled={!selectedAccount || generatingEvidence || (licenseStatus && !licenseStatus.valid)}
                    sx={{
                      bgcolor: '#7b1fa2',
                      '&:hover': { bgcolor: '#6a1b9a' },
                      minWidth: 250
                    }}
                  >
                    {generatingEvidence ? 'Generating Evidence...' : 'Generate Evidence with AI'}
                  </Button>
                </Box>

                {generatingEvidence && (
                  <Box sx={{ mt: 3 }}>
                    <LinearProgress variant="determinate"
                      value={Math.round((checklist.filter(c => c.status !== 'PENDING').length / checklist.length) * 100)} />
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                      Assessed {checklist.filter(c => c.status !== 'PENDING').length} of {checklist.length} checks...
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Evidence History Dialog (All Frameworks) */}
        <Dialog
          open={showHistory}
          onClose={() => setShowHistory(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">All Evidence History</Typography>
              {evidenceHistory.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleClearHistory}
                >
                  Clear All
                </Button>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            {evidenceHistory.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No evidence history found
                </Typography>
              </Box>
            ) : (
              <List>
                {evidenceHistory.map((item) => {
                  const framework = getFrameworkInfo(item.frameworkId);
                  return (
                    <ListItem
                      key={item.evidenceId}
                      button
                      onClick={() => handleLoadHistoricalEvidence(item.evidenceId)}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': { bgcolor: '#f5f5f5' }
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: framework?.color || '#757575' }}>
                          {framework?.icon ? <RBIcon name={framework.icon} /> : <Assessment />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {framework?.name || item.frameworkId}
                            </Typography>
                            <Chip label={item.modelId?.split('.')[1] || 'AI'} size="small" />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              Account: {item.accountName} ({item.awsAccountId})
                            </Typography>
                            <Typography variant="caption" display="block">
                              Generated: {formatTimestamp(item.createdAt)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip label={`✓ ${item.summary.passed || 0}`} size="small" color="success" />
                              <Chip label={`✗ ${item.summary.failed || 0}`} size="small" color="error" />
                              <Chip label={`⚠ ${item.summary.warnings || 0}`} size="small" color="warning" />
                            </Box>
                          </Box>
                        }
                      />
                      <IconButton
                        edge="end"
                        onClick={(e) => handleDeleteEvidence(item.evidenceId, e)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowHistory(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Framework-Specific Evidence History Dialog */}
        <Dialog
          open={showFrameworkHistory}
          onClose={() => setShowFrameworkHistory(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedFramework && (
                  <Avatar sx={{ bgcolor: selectedFramework.color }}>
                    <RBIcon name={selectedFramework.icon} />
                  </Avatar>
                )}
                <Box>
                  <Typography variant="h6">{selectedFramework?.name} History</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Track compliance changes over time
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {frameworkHistory.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No evidence history for this framework yet
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Generate evidence to start tracking compliance changes
                </Typography>
              </Box>
            ) : (
              <List>
                {frameworkHistory.map((item, index) => {
                  const isLatest = index === 0;
                  return (
                    <ListItem
                      key={item.evidenceId}
                      button
                      onClick={() => {
                        handleLoadHistoricalEvidence(item.evidenceId);
                        setShowFrameworkHistory(false);
                      }}
                      sx={{
                        border: isLatest ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: isLatest ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
                        '&:hover': { bgcolor: isLatest ? 'rgba(25, 118, 210, 0.1)' : '#f5f5f5' }
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: selectedFramework?.color || '#757575' }}>
                          {isLatest ? <Assessment /> : <History />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: isLatest ? 700 : 600 }}>
                              {isLatest ? 'Latest Assessment' : `Assessment #${frameworkHistory.length - index}`}
                            </Typography>
                            {isLatest && <Chip label="Current" size="small" color="primary" />}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              Account: {item.accountName} ({item.awsAccountId})
                            </Typography>
                            <Typography variant="caption" display="block">
                              Generated: {formatTimestamp(item.createdAt)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip label={`✓ ${item.summary.passed || 0}`} size="small" color="success" />
                              <Chip label={`✗ ${item.summary.failed || 0}`} size="small" color="error" />
                              <Chip label={`⚠ ${item.summary.warnings || 0}`} size="small" color="warning" />
                              <Chip 
                                label={`${item.summary.complianceScore || 0}% Compliant`} 
                                size="small" 
                                sx={{ 
                                  bgcolor: item.summary.complianceScore >= 80 ? '#4caf50' : item.summary.complianceScore >= 60 ? '#ff9800' : '#f44336',
                                  color: 'white'
                                }}
                              />
                            </Box>
                          </Box>
                        }
                      />
                      <IconButton
                        edge="end"
                        onClick={(e) => handleDeleteEvidence(item.evidenceId, e)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowFrameworkHistory(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Status Explanation Dialog */}
        <Dialog
          open={statusDialog.open}
          onClose={() => setStatusDialog({ open: false, status: null, title: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {statusDialog.status && getStatusExplanation(statusDialog.status).icon}
              <Box>
                <Typography variant="h6" sx={{ color: statusDialog.status && getStatusExplanation(statusDialog.status).color }}>
                  {statusDialog.status && getStatusExplanation(statusDialog.status).title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statusDialog.title}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {statusDialog.status && getStatusExplanation(statusDialog.status).description}
            </Typography>
            
            {statusDialog.status === 'PASS' && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Next Steps:</strong> No action required. Continue monitoring this control in future assessments.
                </Typography>
              </Alert>
            )}
            
            {statusDialog.status === 'FAIL' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Next Steps:</strong> Review the affected resources and follow the remediation steps provided. Click "View Steps" in the Remediation column for detailed instructions.
                </Typography>
              </Alert>
            )}
            
            {statusDialog.status === 'WARNING' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Next Steps:</strong> Investigate the warning and determine if action is needed. Review the evidence and affected resources.
                </Typography>
              </Alert>
            )}
            
            {statusDialog.status === 'ERROR' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Next Steps:</strong> Check AWS credentials, IAM permissions, and network connectivity. Ensure the IAM role has necessary permissions for the service being assessed.
                </Typography>
              </Alert>
            )}
            
            {(!statusDialog.status || statusDialog.status === 'PENDING') && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Next Steps:</strong> Select an AWS account, then click "Generate Evidence with AI" to assess this control.
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusDialog({ open: false, status: null, title: null })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remediation Dialog */}
        <Dialog
          open={remediationDialog.open}
          onClose={() => setRemediationDialog({ open: false, check: null })}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'background.paper'
            }
          }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Build color="primary" />
              <Box>
                <Typography variant="h6">Remediation Steps</Typography>
                <Typography variant="caption" color="text.secondary">
                  {remediationDialog.check?.title}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {remediationDialog.check && (
              <Box>
                {/* Downtime Warning - Always show */}
                <Alert 
                  severity={
                    remediationDialog.check.downtimeRequired?.toLowerCase().includes('yes') 
                      ? 'warning' 
                      : remediationDialog.check.downtimeRequired?.toLowerCase().includes('no')
                      ? 'success'
                      : 'info'
                  }
                  icon={remediationDialog.check.downtimeRequired?.toLowerCase().includes('yes') ? <Warning /> : <Info />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    ⏱️ Downtime Impact: {
                      remediationDialog.check.downtimeRequired 
                        ? remediationDialog.check.downtimeRequired.split('-')[0].trim()
                        : 'Check AWS documentation'
                    }
                  </Typography>
                  <Typography variant="body2">
                    {remediationDialog.check.downtimeRequired 
                      ? remediationDialog.check.downtimeRequired.split('-').slice(1).join('-').trim()
                      : 'Review the impact before applying changes to production systems.'
                    }
                  </Typography>
                </Alert>

                {/* Remediation Steps */}
                {remediationDialog.check.remediationSteps && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Build fontSize="small" /> Step-by-Step Instructions
                    </Typography>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
                        borderRadius: 2
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          whiteSpace: 'pre-wrap',
                          color: 'text.primary',
                          lineHeight: 1.7
                        }}
                      >
                        {remediationDialog.check.remediationSteps}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* CLI Commands */}
                {remediationDialog.check.cliCommands && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Code fontSize="small" /> AWS CLI Commands
                    </Typography>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#1e1e1e' : '#263238',
                        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#1a1a1a'}`,
                        borderRadius: 2
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          color: '#4caf50',
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.85rem',
                          lineHeight: 1.6
                        }}
                      >
                        {remediationDialog.check.cliCommands}
                      </Typography>
                    </Paper>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Code />}
                      onClick={() => {
                        navigator.clipboard.writeText(remediationDialog.check.cliCommands);
                        showNotification('CLI commands copied to clipboard', 'success');
                      }}
                      sx={{ mt: 1.5 }}
                    >
                      Copy Commands
                    </Button>
                  </Box>
                )}

                {/* Recommendation */}
                {remediationDialog.check.recommendation && (
                  <Box sx={{ mt: 3 }}>
                    <Alert severity="info" icon={<Info />}>
                      <Typography variant="body2" sx={{ color: 'text.primary' }}>
                        <strong>Recommendation:</strong> {remediationDialog.check.recommendation}
                      </Typography>
                    </Alert>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => setRemediationDialog({ open: false, check: null })}
              variant="contained"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Admin: Upload Checklist Document(s) Dialog — supports multiple files */}
        <Dialog
          open={uploadChecklistDialog}
          onClose={() => !uploadChecklistLoading && setUploadChecklistDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <UploadFile color="primary" />
              <Box>
                <Typography variant="h6">Upload Checklist Documents</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedFramework?.name} — select one or more files; checks from all documents are accumulated
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Upload PDF, DOCX, CSV, JSON, or TXT files (up to 10 at once, 100 MB each).
              Checks from all documents are merged — existing checks are updated, manually added checks are preserved.
              {uploadChecklistLoading && <><br/><strong>Large PDFs can take 20–40 min per file.</strong> Processing continues in the background if you close this dialog.</>}
            </Alert>

            {/* Multi-file picker */}
            <input
              ref={checklistFileInputRef}
              type="file"
              accept=".pdf,.docx,.csv,.json,.txt"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => {
                setUploadChecklistFiles(Array.from(e.target.files || []));
                setUploadChecklistResult(null);
              }}
            />
            <Box
              sx={{
                border: '2px dashed',
                borderColor: uploadChecklistFiles.length > 0 ? 'success.main' : 'divider',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: uploadChecklistFiles.length > 0 ? '#f1f8e9' : 'background.default',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => checklistFileInputRef.current?.click()}
            >
              {uploadChecklistFiles.length > 0 ? (
                <Box>
                  <InsertDriveFile sx={{ fontSize: 36, color: 'success.main', mb: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {uploadChecklistFiles.length} file{uploadChecklistFiles.length !== 1 ? 's' : ''} selected
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                    {uploadChecklistFiles.map((f, i) => (
                      <Chip key={i} label={f.name} size="small" variant="outlined" color="success"
                        sx={{ fontSize: '0.7rem' }} />
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Click to change selection
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <CloudUpload sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to select files (PDF, DOCX, CSV, JSON, TXT — up to 10 files, 100 MB each)
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Result feedback */}
            {uploadChecklistResult && (
              <Alert severity={uploadChecklistResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                {uploadChecklistResult.success
                  ? `${uploadChecklistResult.checksCount} checks parsed from ${uploadChecklistFiles.length - (uploadChecklistResult.failedCount || 0)} file(s) — ${uploadChecklistResult.totalChecks} total active checks`
                  : uploadChecklistResult.message}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setUploadChecklistDialog(false)} disabled={uploadChecklistLoading}>
              {uploadChecklistResult?.success ? 'Close' : 'Cancel'}
            </Button>
            {!uploadChecklistResult?.success && (
              <Button
                variant="contained"
                startIcon={uploadChecklistLoading ? <CircularProgress size={16} color="inherit" /> : <UploadFile />}
                onClick={handleUploadChecklist}
                disabled={uploadChecklistFiles.length === 0 || uploadChecklistLoading}
              >
                {uploadChecklistLoading
                  ? `Parsing... ${uploadElapsedSecs < 60 ? uploadElapsedSecs + 's' : Math.floor(uploadElapsedSecs / 60) + 'm ' + (uploadElapsedSecs % 60) + 's'}`
                  : `Upload & Parse${uploadChecklistFiles.length > 1 ? ` (${uploadChecklistFiles.length} files)` : ''}`}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Admin: Assign Frameworks to Users Dialog */}
        <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Security color="primary" />
              <Box>
                <Typography variant="h6">Assign Frameworks to Users</Typography>
                <Typography variant="caption" color="text.secondary">
                  Select a user, then toggle which frameworks they can access
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {/* User selector */}
            <FormControl fullWidth size="small" sx={{ mb: 3, mt: 1 }}>
              <InputLabel>Select User</InputLabel>
              <Select
                value={assignSelectedUser}
                label="Select User"
                onChange={(e) => loadUserAssignments(e.target.value)}
              >
                {assignUsers.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.email}{u.company_name ? ` — ${u.company_name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {assignSelectedUser && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Toggle frameworks on/off for this user. Changes take effect immediately.
                </Typography>
                <Grid container spacing={1.5}>
                  {complianceFrameworks.map(fw => {
                    const assigned = assignUserFrameworks.includes(fw.id);
                    return (
                      <Grid item xs={12} sm={6} md={4} key={fw.id}>
                        <Paper
                          onClick={() => !assignSaving && handleToggleFrameworkForUser(fw.id)}
                          sx={{
                            p: 1.5,
                            cursor: assignSaving ? 'wait' : 'pointer',
                            border: '2px solid',
                            borderColor: assigned ? fw.color : 'divider',
                            bgcolor: assigned ? `${fw.color}15` : 'background.paper',
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: fw.color, boxShadow: 1 }
                          }}
                        >
                          <Avatar sx={{ bgcolor: assigned ? fw.color : '#e0e0e0', width: 32, height: 32 }}>
                            <RBIcon name={fw.icon} />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.2 }}>
                              {fw.name}
                            </Typography>
                            {fw.total_checks > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {fw.total_checks} checks
                              </Typography>
                            )}
                          </Box>
                          {assigned
                            ? <CheckCircle sx={{ color: fw.color, fontSize: 20, flexShrink: 0 }} />
                            : <CheckBoxOutlineBlank sx={{ color: '#bdbdbd', fontSize: 20, flexShrink: 0 }} />
                          }
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={`${assignUserFrameworks.length} framework${assignUserFrameworks.length !== 1 ? 's' : ''} assigned`}
                    color={assignUserFrameworks.length > 0 ? 'primary' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </>
            )}

            {!assignSelectedUser && assignUsers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No non-admin users found.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAssignDialog(false)} variant="contained">Done</Button>
          </DialogActions>
        </Dialog>

        {/* Admin: Add Framework Dialog */}
        <Dialog open={addFrameworkDialog} onClose={() => !addFrameworkSaving && setAddFrameworkDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Add color="primary" />
              <Box>
                <Typography variant="h6">Add New Framework</Typography>
                <Typography variant="caption" color="text.secondary">
                  Create a new compliance framework visible to all admins
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField
              label="Framework ID *"
              value={addFrameworkForm.framework_id}
              onChange={e => setAddFrameworkForm(f => ({ ...f, framework_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') }))}
              fullWidth size="small"
              placeholder="e.g. iso27001, pci-dss, nist-csf"
              helperText="Lowercase letters, numbers and hyphens only"
            />
            <TextField
              label="Framework Name *"
              value={addFrameworkForm.name}
              onChange={e => setAddFrameworkForm(f => ({ ...f, name: e.target.value }))}
              fullWidth size="small"
              placeholder="e.g. ISO 27001 Information Security"
            />
            <TextField
              label="Description"
              value={addFrameworkForm.description}
              onChange={e => setAddFrameworkForm(f => ({ ...f, description: e.target.value }))}
              fullWidth size="small" multiline rows={2}
              placeholder="Brief description of the framework"
            />
            <TextField
              label="Official Source URL"
              value={addFrameworkForm.official_source_url}
              onChange={e => setAddFrameworkForm(f => ({ ...f, official_source_url: e.target.value }))}
              fullWidth size="small"
              placeholder="https://..."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setAddFrameworkDialog(false)} disabled={addFrameworkSaving}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleAddFramework}
              disabled={addFrameworkSaving || !addFrameworkForm.framework_id.trim() || !addFrameworkForm.name.trim()}
              startIcon={addFrameworkSaving ? <CircularProgress size={16} color="inherit" /> : <Add />}
            >
              {addFrameworkSaving ? 'Adding...' : 'Add Framework'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Admin: Delete Framework Confirmation Dialog */}
        <Dialog open={deleteFrameworkDialog.open} onClose={() => !deleteFrameworkSaving && setDeleteFrameworkDialog({ open: false, framework: null })} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <DeleteOutline color="error" />
              <Typography variant="h6">Delete Framework</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Are you sure you want to delete <strong>{deleteFrameworkDialog.framework?.name}</strong>?
            </Typography>
            <Alert severity="warning" sx={{ mt: 1 }}>
              This will permanently remove the framework and all its associated checks, uploads, and user assignments. This action cannot be undone.
            </Alert>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDeleteFrameworkDialog({ open: false, framework: null })} disabled={deleteFrameworkSaving}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDeleteFramework}
              disabled={deleteFrameworkSaving}
              startIcon={deleteFrameworkSaving ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline />}
            >
              {deleteFrameworkSaving ? 'Deleting...' : 'Delete Framework'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Admin: Add / Edit Check Dialog */}
        <Dialog open={checkDialog.open} onClose={() => !checkSaving && setCheckDialog({ open: false, mode: 'add', check: null })} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {checkDialog.mode === 'add' ? <Add color="primary" /> : <EditIcon color="primary" />}
              <Typography variant="h6">{checkDialog.mode === 'add' ? 'Add Check' : 'Edit Check'}</Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
            <TextField label="Title *" value={checkForm.title} onChange={e => setCheckForm(f => ({ ...f, title: e.target.value }))} fullWidth size="small" />
            <TextField label="Description" value={checkForm.description} onChange={e => setCheckForm(f => ({ ...f, description: e.target.value }))} fullWidth size="small" multiline rows={3} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Severity</InputLabel>
                <Select value={checkForm.severity} label="Severity" onChange={e => setCheckForm(f => ({ ...f, severity: e.target.value }))}>
                  {['CRITICAL','HIGH','MEDIUM','LOW'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="AWS Service" value={checkForm.aws_service} onChange={e => setCheckForm(f => ({ ...f, aws_service: e.target.value }))} size="small" placeholder="e.g. IAM, S3, EC2" sx={{ flex: 1 }} />
            </Box>
            <TextField label="Category" value={checkForm.category} onChange={e => setCheckForm(f => ({ ...f, category: e.target.value }))} fullWidth size="small" placeholder="e.g. Access Control, Encryption" />
            <TextField label="Requirement Section" value={checkForm.requirement_section} onChange={e => setCheckForm(f => ({ ...f, requirement_section: e.target.value }))} fullWidth size="small" placeholder="e.g. Section 4.2.1" />
            <TextField label="Official Reference" value={checkForm.official_reference} onChange={e => setCheckForm(f => ({ ...f, official_reference: e.target.value }))} fullWidth size="small" placeholder="URL or document reference" />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setCheckDialog({ open: false, mode: 'add', check: null })} disabled={checkSaving}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveCheck} disabled={checkSaving || !checkForm.title.trim()}
              startIcon={checkSaving ? <CircularProgress size={16} color="inherit" /> : (checkDialog.mode === 'add' ? <Add /> : <EditIcon />)}>
              {checkSaving ? 'Saving...' : checkDialog.mode === 'add' ? 'Add Check' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Container>
  );
}

export default RegulatoryBot;
